/**
 * Componente PrivacyAccordionItem
 * Elemento de acordeón accesible para agrupar bloques de la Política de
 * Privacidad por colectivo (clientes, recetas, proveedores, etc.).
 *
 * Por qué: la versión de Fluent UI usada en el proyecto (@fluentui/react v8,
 * "Fabric") no incluye un componente Accordion (ese llegó con Fluent UI v9 /
 * @fluentui/react-components, que no está instalado aquí). Para no añadir una
 * dependencia nueva solo para esto, se implementa un patrón de "disclosure"
 * accesible con un <button> nativo y los atributos ARIA recomendados por el
 * patrón WAI-ARIA Accordion (aria-expanded/aria-controls en el disparador,
 * role="region" + aria-labelledby en el panel).
 *
 * Cómo se relaciona: se usa desde RecipePrivacyPage.jsx, una vez por cada
 * colectivo definido en el array de datos `privacyGroups`, manteniendo el
 * contenido (datos) separado de este componente de presentación (SRP).
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Icono de chevron inline (evita depender de @fluentui/react-icons para un
 * único glifo y mantiene el mismo enfoque de SVG inline que ya usa
 * PrivacyPage.jsx para su icono de flecha).
 * @param {Object} props - Propiedades del icono
 * @param {boolean} props.expanded - Si el panel asociado está abierto
 * @returns {JSX.Element} Icono de chevron
 */
const ChevronIcon = ({ expanded }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-5 w-5 shrink-0 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

ChevronIcon.propTypes = {
  expanded: PropTypes.bool.isRequired,
};

/**
 * Elemento individual de acordeón: cabecera pulsable + panel colapsable.
 *
 * @param {Object} props - Propiedades del componente
 * @param {string} props.id - Identificador único del grupo (se usa para el
 *   anchor de la URL, p.ej. `/privacidad-recetas#recetas`, y para enlazar
 *   el botón con su panel vía aria-controls/aria-labelledby)
 * @param {string} props.title - Título del colectivo (se muestra en mayúsculas)
 * @param {boolean} [props.defaultOpen] - Si el panel debe iniciar abierto
 * @param {React.ReactNode} props.children - Contenido del panel
 * @returns {JSX.Element} Elemento de acordeón
 */
const PrivacyAccordionItem = ({ id, title, defaultOpen, children }) => {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const buttonId = `${id}-accordion-button`;
  const panelId = `${id}-accordion-panel`;

  return (
    <div
      id={id}
      className="border border-intercasa-dark/10 rounded-2xl overflow-hidden bg-white scroll-mt-32"
    >
      <h3 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => setIsExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-baskerville text-lg md:text-xl font-bold text-intercasa-dark hover:bg-intercasa-bone transition-colors duration-200"
        >
          <span>{title}</span>
          <ChevronIcon expanded={isExpanded} />
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!isExpanded}
        className="px-6 pb-6 pt-1 border-t border-intercasa-dark/10"
      >
        {children}
      </div>
    </div>
  );
};

PrivacyAccordionItem.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

PrivacyAccordionItem.defaultProps = {
  defaultOpen: false,
};

export default PrivacyAccordionItem;
